// Wrap a host ReportRuntimeAdapter so a set of externally-resolved parameter
// values ("fixed" params — a transaction row via an action's param_map, a
// smart-page embed's page-variable args, or a dashboard tile's criteria
// bindings) override the report's own defaults. Prompt names match
// case-insensitively so binding authors never have to guess the canonical
// casing. User-edited criteria values still win over the fixed layer on a
// re-run — the fixed value only fills prompts the form left empty.

import type {
  ReportDefinition, ReportOutputFormat, ReportRenderOutcome, ReportRuntimeAdapter,
} from './types'

export function withFixedParams(
  adapter: ReportRuntimeAdapter,
  fixed: Record<string, unknown>,
): ReportRuntimeAdapter {
  const fixedLower: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(fixed)) fixedLower[k.toLowerCase()] = v
  const canonical = (names: string[]): Record<string, unknown> => {
    // Re-key the fixed values onto the definition's canonical prompt names.
    const out: Record<string, unknown> = {}
    for (const n of names) {
      const v = fixedLower[n.toLowerCase()]
      if (v !== undefined) out[n] = v
    }
    return out
  }
  return {
    async fetchDefinition(): Promise<ReportDefinition> {
      const d = await adapter.fetchDefinition()
      const names = d.promptVars.map(p => p.name)
      const applied = canonical(names)
      const resolvedParams = { ...d.resolvedParams, ...applied }
      const missingParams = d.missingParams.filter(m => applied[m] === undefined)
      return { ...d, resolvedParams, missingParams, needsInput: missingParams.length > 0 }
    },
    async render(
      params: Record<string, unknown>, format: ReportOutputFormat,
    ): Promise<ReportRenderOutcome> {
      // User-edited values win over the fixed binding ONLY where the user
      // actually changed something; merge fixed values in for prompts the
      // form left empty.
      const merged = { ...params }
      for (const [k, v] of Object.entries(fixedLower)) {
        const hit = Object.keys(merged).find(mk => mk.toLowerCase() === k)
        if (!hit || merged[hit] === undefined || merged[hit] === null || merged[hit] === '') {
          merged[hit || k] = v
        }
      }
      return adapter.render(merged, format)
    },
    criteria: adapter.criteria,
  }
}

export default withFixedParams
